<template>
  <div>
    <ExpenseGroupAddModal :open="add_group" @created="handleGroup" @close="add_group = false"/>
    <div class="grid grid-cols-1 lg:grid-cols-2 col-gap-12 row-gap-11 items-center">
      <div class="lg:col-span-2 flex items-center" v-if="!edit_item">
        <div class="text-xl font-bold text-text leading-tight ">{{ $t('formTitle') }}</div>
      </div>
      <BaseInput v-model.trim="$v.title.$model"
                 :label="$t('nameLabel')"
                 :class="{'--error':$v.title.$error }">
        <template v-slot:errors="">
          <div class="error" v-if="!$v.title.required">{{ $t('errors.required') }}</div>
          <div class="error" v-else-if="!$v.title.noSpecSymbols">{{ $t('errors.noSpecSymbols') }}</div>
        </template>
      </BaseInput>
      <div class="col-start-1">
        <MoreSettings class="ml-3 px-2 relative z-10"
                      :class="more_settings && 'bg-block'"
                      @click="toggleMoreSettings(!more_settings)"
                      :active="more_settings"/>
      </div>
      <div v-if="more_settings"
           class="col-span-1 lg:col-span-2 grid grid-cols-1 lg:grid-cols-2 col-gap-12 row-gap-12 relative
                border rounded border-border
                p-10 -mt-14">
        <div class="grid row-gap-11 -mx-0-5 pr-40">
          <IconPicker
              :selected="icon"
              @change="handleIconSelect">
            {{ $t('iconPickText') }}
          </IconPicker>
          <div class="relative -mr-40 pr-40">
            <BaseSelect
                v-model="group_id"
                :options="expense_groups.data"
                label="title"
                track-by="id"
                :select-label="$t('groupLabel')"
                :allow-empty="false"
                :searchable="false"
                :show-labels="false"
                @input="handleGroup"/>
            <div class="absolute right-0 top-4 m-auto
                      border-b border-dashed border-opacity-50 border-text-ternary
                      text-2sm text-text-ternary hover:text-red hover:border-red
                      transition duration-200 cursor-pointer"
                 @click="add_group = true">
              {{ $t('createGroupLabel') }}
            </div>
          </div>
        </div>
        <div class="flex flex-col pt-2 space-y-6">
          <BaseCheckbox v-if="fullSettings" :checked="shift_plus" @change="shift_plus = $event">
            {{ $t('checkShiftPlus') }}
          </BaseCheckbox>
          <BaseCheckbox v-if="fullSettings" :checked="shift_minus" @change="shift_minus = $event">
            {{ $t('checkShiftMinus') }}
          </BaseCheckbox>
        </div>
      </div>
      <div v-if="!edit_item" class="lg:col-span-2 grid grid-cols-2 items-center col-gap-12">
        <BaseButton theme="red"
                    class="w-78 lg:w-91 mr-8"
                    :loading="submitted"
                    @click="addExpense">
          {{ $t('createCategory') }}
        </BaseButton>
      </div>

      <div v-else class="lg:col-span-2 flex flex-wrap items-center">
        <BaseButton @click="save"
                    class="w-78 lg:w-91 mr-8"
                    theme="gray"
                    :loading="submitted">
          {{ $t('saveChanges') }}
        </BaseButton>
        <BaseButton @click="$emit('cancelEdit')"
                    :loading="submitted"
                    class="w-50 lg:w-68 mr-20"
                    theme="white">
          {{ $t('cancel') }}
        </BaseButton>
        <ArchiveBtn v-if="expenses.data.length !== 1"
                    class="ml-auto"
                    :disabled="edit_item.is_default"
                    :disabled-tooltip="$t('disabledRemoveTooltipIsSystem')"
                    :submitted="submitted"
                    @confirm="archive">
          <template v-slot:text>
            {{ $t('deleteLabel') }}
          </template>
        </ArchiveBtn>
      </div>
    </div>
  </div>
</template>

<script>

import {helpers, required} from 'vuelidate/lib/validators'
import {mapActions, mapState} from "vuex";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import MoreSettings from "@/components/common/MoreSettings";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import ExpenseGroupAddModal from "@/components/cabinet/SpaceSettings/ExpenseSettings/ExpenseGroupAddModal";
import IconPicker from "@/components/common/IconPicker/IconPicker";
import BaseCheckbox from "@/components/UI/BaseCheckbox";
import ArchiveBtn from "@/components/UI/ArchiveBtn";

const noSpecSymbols = helpers.regex('noSpecSymbols', /^[^*|\/":<>[\]{}`\\()';@&!$]+$/)

export default {
  name: "ExpenseSettingsForm",
  components: {
    ExpenseGroupAddModal,
    ArchiveBtn,
    BaseCheckbox,
    IconPicker,
    BaseButton,
    BaseSelect,
    MoreSettings,
    BaseInput,
  },
  props: {
    edit_item: {
      type: Object,
      default: null
    },
    fullSettings: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      add_group: false,
      more_settings: !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false,
      submitted: false,

      title: '',
      icon: 'chart_pie',
      group_id: {id: null, title: this.$t('woGroup')},
      shift_minus: false,
      shift_plus: false,
    }
  },
  validations: {
    title: {
      required,
      noSpecSymbols,
    },
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/expenses', ['expenses', 'expense_groups']),
  },
  methods: {
    ...mapActions('space/expenses', ['getExpenses', 'createExpense', 'updateExpense', 'archiveExpense']),
    toggleMoreSettings(val) {
      localStorage.setItem('settings', val ? '1' : '0');
      this.more_settings = val;
    },
    handleIconSelect(val) {
      this.icon = val;
    },
    handleCategory(val) {
      this.parent_id = val;
    },
    handleGroup(val) {
      this.group_id = val;
      if (this.add_group) {
        this.add_group = false;
      }
      this.$emit('expenseGroupCreated')
    },
    addExpense() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          space_id: this.current_space.data.id,
          title: this.title,
          icon: this.icon,
          group_id: this.group_id.id,
          shift_minus: +this.shift_minus,
          shift_plus: +this.shift_plus
        };
        this.createExpense(data).then((res) => {
          if (res.body.success) {
            this.clearFields();
            this.$v.$reset();
            this.$toasted.success('Категория расходов добавлена')
            this.$emit('expenseCreated')
          } else {
            this.$toasted.error('Произошла ошибка')
            console.log(res)
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        document.documentElement.scrollTop = 0;
        this.$toasted.error('Проверьте поля')
      }
    },
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          id: this.edit_item.id,
          title: this.title,
          icon: this.icon,
          group_id: this.group_id.id,
          shift_minus: +this.shift_minus,
          shift_plus: +this.shift_plus
        };
        this.updateExpense(data).then(res => {
          if (res.body.success) {
            this.getExpenses();
            this.clearFields();
            this.$v.$reset();
            this.$emit('cancelEdit')
            this.$toasted.success('Категория расходов изменена')
          } else {
            console.log(res);
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        this.$toasted.error('Проверьте поля')
      }
    },
    archive() {
      this.submitted = true;
      this.archiveExpense(this.edit_item.id).then(res => {
        if (res.body.success) {
          this.getExpenses();
          this.clearFields();
          this.$v.$reset();
          this.$emit('cancelEdit');
          this.$toasted.info('Категория расходов удалена')
        } else {
          console.log(res)
        }
      }).finally(() => {
        this.submitted = false;
      });
    },
    clearFields() {
      this.title = '';
      this.more_settings = !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false;
      this.icon = 'chart_pie';
      this.group_id = {id: null, title: this.$t('woGroup')};
      this.shift_minus = false;
      this.shift_plus = false;
    },
    setFields(item) {
      this.more_settings = true;
      this.title = item.title;
      this.icon = item.icon;
      this.group_id = !!item.group_id ? this.expense_groups.data.filter(i => item.group_id === i.id)[0] : {
        id: null,
        title: this.$t('woGroup')
      };
      this.shift_minus = !!item.shift_minus;
      this.shift_plus = !!item.shift_plus;
    }
  },
  watch: {
    edit_item: {
      immediate: true,
      handler: function (newVal) {
        if (!!newVal) {
          this.setFields(newVal)
        }
      }
    }
  },
  i18n: {
    messages: {
      en: {
        formTitle: 'Add expense category',
        nameLabel: 'Category name',
        iconPickText: 'Pick an icon for expense',
        groupLabel: 'Expense group',
        woGroup: 'Without group',
        createGroupLabel: 'Create group',
        checkShiftPlus: 'Shift Unspent Budget',
        checkShiftMinus: 'Subtract budget overruns in next',
        createCategory: 'Create expense',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        deleteLabel: 'Delete category',
        disabledRemoveTooltipIsSystem: 'Unable to delete, is a system category.'
      },
      ru: {
        formTitle: 'Добавление категории расходов',
        nameLabel: 'Название категории',
        iconPickText: 'Выберите иконку для категории',
        groupLabel: 'Группа расходов',
        woGroup: 'Без группы',
        createGroupLabel: 'Создать группу',
        checkShiftPlus: 'Перенос неизрасходованного бюджета',
        checkShiftMinus: 'Вычет перерасхода бюджета в следующем',
        createCategory: 'Добавить категорию',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        deleteLabel: 'Удалить категорию',
        disabledRemoveTooltipIsSystem: 'Невозможно удалить, является системной категорией.'
      }
    }
  }
}
</script>
